import React, { useEffect, useRef, useState } from 'react';
import axios from '../../utils/axios';
import Sidebar from './sidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUser } from '../../utils/auth';
import toast, { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import resolveImageUrl from '../../utils/imageUtils';

const SHOW_ACTIVITY_HISTORY = false;

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};

const toFiniteNumber = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  const n = Number(String(val).replaceAll(",", "").trim());
  return Number.isFinite(n) ? n : null;
};

const formatTwoDecimals = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";
  const lowered = str.toLowerCase();
  if (lowered === "null" || lowered === "undefined") return "";
  const n = toFiniteNumber(str);
  if (n === null) return str;
  return String(Math.round(n));
};

const parseRange = (rangeStr) => {
  if (!rangeStr) return null;
  const str = String(rangeStr).toLowerCase().trim();
  const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

  let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'greater', value: Number(match[1]) };
  }

  match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'less', value: Number(match[1]) };
  }

  match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return {
      type: 'between',
      min: Number(match[1]),
      max: Number(match[2])
    };
  }

  const num = Number(cleanStr);
  if (Number.isFinite(num)) {
    return { type: 'exact', value: num };
  }

  return null;
};

const hasUsableValue = (value) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim().toLowerCase();
  return str !== "" && str !== "null" && str !== "undefined" && str !== "n/a";
};

const pickFirstValue = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (hasUsableValue(value)) return value;
  }
  return "";
};

const getMLDisplayValue = (property, field) => {
  let realValue = "";

  switch (field) {
    case 'floor':
      realValue = pickFirstValue(property, ["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
      break;
    case 'landuse':
      realValue = pickFirstValue(property, ["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
      break;
    case 'landarea':
      {
        const ml = pickFirstValue(property, ["landarea_ml", "land_area_ml"]);
        const bucket = pickFirstValue(property, ["landarea_combined_pred"]);
        let n;
        try {
          n = Number(String(ml ?? "").replaceAll(",", "").trim());
        } catch (e) {
          n = NaN;
        }
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
      }
      break;
    case 'coveredarea':
      {
        const ml = pickFirstValue(property, ["coveredarea_ml", "covered_area_ml"]);
        const bucket = pickFirstValue(property, ["coveredarea_combined_pred"]);
        let n;
        try {
          n = Number(String(ml ?? "").replaceAll(",", "").trim());
        } catch (e) {
          n = NaN;
        }
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
      }
      break;
  }

  if (hasUsableValue(realValue)) {
    if (field === "landarea" || field === "coveredarea") {
      const strVal = String(realValue ?? "");
      const isRange = strVal.toLowerCase().includes("greater") ||
        strVal.toLowerCase().includes("less") ||
        strVal.toLowerCase().includes("between");
      if (isRange) {
        return realValue;
      }
      const formatted = formatTwoDecimals(realValue);
      return formatted || "N/A";
    }
    const num = Number(realValue);
    if (Number.isFinite(num) && !isNaN(num)) {
      return Number.isInteger(num) ? num : Math.round(num);
    }
    return realValue;
  }
  return 'N/A';
};

const areValuesDifferent = (adminVal, mlVal, field) => {
  const normalize = (val) => {
    if (val === null || val === undefined) return null;
    const raw = String(val).trim();
    if (!raw) return null;
    const lowered = raw.toLowerCase();
    if (lowered === "n/a" || lowered === "null" || lowered === "undefined") return null;
    if (field === "landarea" || field === "coveredarea") {
      const numStr = raw.replace(/[^0-9.]/g, '');
      const numeric = Number(numStr);
      if (Number.isFinite(numeric)) return numeric;
    }
    const numeric = Number(raw.replaceAll(",", ""));
    if (Number.isFinite(numeric)) return numeric;
    return lowered;
  };

  const a = normalize(adminVal);

  if ((field === "landarea" || field === "coveredarea") && mlVal) {
    const range = parseRange(mlVal);
    if (range && typeof a === "number") {
      switch (range.type) {
        case "greater":
          return a <= range.value;
        case "less":
          return a >= range.value;
        case "between":
          return a < range.min || a > range.max;
        case "exact":
          return Math.abs(a - range.value) > 1e-9;
      }
    }
  }

  const m = normalize(mlVal);
  if (a === null || m === null) return false;
  if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
  return a !== m;
};

const FlaggedByAI = () => {
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'flagged-not-reassessed' | 'flagged-reassessed'
  const [selectedPropertyOrigin, setSelectedPropertyOrigin] = useState('algorithm');
  const [highlightPin, setHighlightPin] = useState("");
  const [propertyTimeline, setPropertyTimeline] = useState({ timeline: [] });
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const searchParams = new URLSearchParams(location.search);
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";
  const [autoOpenedPin, setAutoOpenedPin] = useState("");
  const user = getUser();



  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const dedupeByPin = (list) => {
    const seen = new Set();
    return (list || []).filter((p) => {
      const key = normalizePin(p?.pin);
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const getPt5Reference = (property) =>
    property?.refno ?? property?.pt5 ?? property?.pt_5 ?? property?.pt ?? "";

  const highlightPropertyCard = (pin) => {
    const normalized = normalizePin(pin);
    if (!normalized) return;
    setHighlightPin(normalized);
    setTimeout(() => {
      const el = document.querySelector(`[data-pin="${normalized}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const navigate = useNavigate();

  const closeDetailModal = () => {
    const pinToHighlight = selectedProperty?.pin;
    setShowDetailModal(false);
    setRotation(0);
    highlightPropertyCard(pinToHighlight);
    navigate({ search: "" }, { replace: true });
  };

  const fetchFlaggedProperties = async () => {
    try {
      setLoading(true);
      // const res = await axios.get(`/inspector/algorithm-flagged-all-filtered`);
      // setProperties(dedupeByPin(res.data || []));
      const res = await axios.get(`/inspector/algorithm-flagged-all-filtered`);
      const fetchBust = Date.now();
      setProperties(dedupeByPin(res.data || []).map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust })));
    } catch (err) {
      console.error('Error fetching algorithm-flagged properties:', err);
      toast.error('Failed to load flagged properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProperties = async () => {
    try {
      const res = await axios.get("/properties", { params: { limit: 10000 } });
      const list = res.data?.data || [];
      const seen = new Set();
      const deduped = list.filter((p) => {
        const key = String(p?.pin || "").trim().toLowerCase();
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setAllProperties(deduped);
    } catch (err) {
      console.error("Failed to fetch all properties:", err);
    }
  };

  useEffect(() => {
    fetchFlaggedProperties();
    fetchAllProperties();
  }, []);

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);

    try {
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.property_id}`, {
          pin: selectedProperty.pin,
          rotateBy: 90,
          forcePortrait: false
        });
      }

      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      if (selectedImage && selectedProperty.image && selectedImage.includes(resolveImageUrl(selectedProperty.image))) {
        setSelectedImage(appendCacheBuster(resolveImageUrl(selectedProperty.image), bust));
      }
      setRotation(0);
      toast.success("Image saved successfully!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", {
        id: toastId,
      });
    } finally {
      setSavingImageRotation(false);
    }
  };

  const handleAccept = async () => {
    try {
      console.log("Accepting property:", selectedProperty);
      if (selectedPropertyOrigin === "algorithm") {
        await axios.post("/inspector/algorithm-flag-accept", {
          propertyId: selectedProperty.property_id || selectedProperty.id,
          userId: user.id || user.userId
        });
      }
      toast.success("Property accepted!");
      fetchFlaggedProperties();
      fetchAllProperties();
      setShowDetailModal(false);
    } catch (err) {
      console.error("Error accepting property:", err);
      toast.error(err.response?.data?.error || "Failed to accept property");
    }
  };

  const handleRejectClick = () => {
    setShowRejectModal(true);
    setRejectReason("");
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please enter a reason for rejection!");
      return;
    }
    try {
      console.log("Rejecting property:", selectedProperty);
      if (selectedPropertyOrigin === "algorithm") {
        await axios.post("/inspector/algorithm-flag-reject", {
          propertyId: selectedProperty.property_id || selectedProperty.id,
          pin: selectedProperty.pin,
          userId: user.id || user.userId,
          comment: rejectReason
        });
      }
      toast.success("Property rejected, sent back to inspector!");
      fetchFlaggedProperties();
      fetchAllProperties();
      setShowRejectModal(false);
      setShowDetailModal(false);
      fetchPropertyTimeline(selectedProperty.pin);
    } catch (err) {
      console.error("Error rejecting property:", err);
      toast.error(err.response?.data?.error || "Failed to reject property");
    }
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setRejectReason("");
  };

  const isPendingInspectorResubmission = (p) =>
    p.algorithm_rejected_by_supervisor === 1 || p.algorithm_rejected_by_supervisor === "1";

  const flaggedReassessedProperties = allProperties.filter(
    (p) =>
      p.status === "flagged" &&
      (p.reassess === 1 || p.reassess === "1") &&
      !isPendingInspectorResubmission(p) &&
      !p.supervisor_flag_comment && // exclude supervisor flagged
      !p.flagged_by // also exclude if flagged by any user (supervisor)
  );
  const flaggedNotReassessedProperties = allProperties.filter(
    (p) =>
      p.status === "flagged" &&
      !(p.reassess === 1 || p.reassess === "1") &&
      !isPendingInspectorResubmission(p) &&
      !p.supervisor_flag_comment && // exclude supervisor flagged
      !p.flagged_by // also exclude if flagged by any user (supervisor)
  );


  const getConfidenceColor = (score) => {
    if (score >= 0.9) return 'text-red-600 bg-red-50';
    if (score >= 0.8) return 'text-orange-600 bg-orange-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  const getFilteredProperties = () => {
    if (activeTab === "flagged-reassessed") return flaggedReassessedProperties;
    if (activeTab === "flagged-not-reassessed") return flaggedNotReassessedProperties;

    if (filterStatus === 'responded') {
      return properties.filter(p => p.inspector_response);
    } else if (filterStatus === 'pending') {
      return properties.filter(p => !p.inspector_response);
    }
    return properties;
  };

  const filteredProperties = getFilteredProperties();
  const respondedCount = (properties || []).filter(p => p.inspector_response).length || 0;
  const pendingCount = (properties || []).filter(p => !p.inspector_response).length || 0;

  const fetchPropertyTimeline = async (pin) => {
    try {
      const res = await axios.get(`/properties/timeline/${pin}`);
      setPropertyTimeline(res.data);
    } catch (err) {
      console.error("Failed to fetch property timeline:", err);
      setPropertyTimeline({ timeline: [] });
    }
  };

  useEffect(() => {
    if (!targetPinFromQuery) return;
    if (!properties.length) return;
    if (autoOpenedPin && autoOpenedPin === targetPinFromQuery) return;

    const normalizedTarget = targetPinFromQuery.toLowerCase();
    const matched = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === normalizedTarget
    );

    if (matched) {
      if (filterStatus !== "all") setFilterStatus("all");
      setHighlightPin(normalizePin(matched.pin));
      setSelectedProperty(matched);
      setShowDetailModal(true);
      fetchPropertyTimeline(matched.pin);
      setAutoOpenedPin(targetPinFromQuery);
    }
  }, [targetPinFromQuery, properties, autoOpenedPin, filterStatus]);

  const toFiniteNumber = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    const n = Number(String(val).replaceAll(",", "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "null" && str !== "undefined" && str !== "n/a";
  };



  const parseRange = (rangeStr) => {
    if (!rangeStr) return null;
    const str = String(rangeStr).toLowerCase().trim();

    const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

    let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'greater', value: Number(match[1]) };
    }

    match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'less', value: Number(match[1]) };
    }

    match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return {
        type: 'between',
        min: Number(match[1]),
        max: Number(match[2])
      };
    }

    const num = Number(cleanStr);
    if (Number.isFinite(num)) {
      return { type: 'exact', value: num };
    }

    return null;
  };

  const areComparableValuesDifferent = (adminVal, mlVal, field) => {
    const toComparable = (val) => {
      if (val === null || val === undefined) return null;
      let raw = String(val).trim();
      if (!raw) return null;
      raw = raw.replace(/sq\.?\s*(yards?|yds?|ft|feet?)/gi, '').replace(',', '').trim();
      const lowered = raw.toLowerCase();
      if (lowered === 'n/a' || lowered === 'null' || lowered === 'undefined') return null;
      const numeric = Number(raw);
      if (Number.isFinite(numeric)) return numeric;
      return lowered;
    };

    if ((field === "landarea" || field === "coveredarea") && mlVal) {
      const range = parseRange(formatMLValue(mlVal));
      const a = toComparable(adminVal);
      if (range && typeof a === "number") {
        switch (range.type) {
          case "greater":
            return !(a > range.value);
          case "less":
            return !(a < range.value);
          case "between":
            const inRange = a >= range.min && a <= range.max;
            return !inRange;
          case "exact":
            const exactMatch = Math.abs(a - range.value) <= 1e-9;
            return !exactMatch;
        }
      }
    }

    const a = toComparable(adminVal);
    const m = toComparable(formatMLValue(mlVal));
    if (a === null || m === null) return false;
    if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
    return a !== m;
  };

  const formatMLValue = (val) => {
    if (!hasUsableValue(val)) return "N/A";
    const strVal = String(val);
    const isRange = strVal.toLowerCase().includes("greater") ||
      strVal.toLowerCase().includes("less") ||
      strVal.toLowerCase().includes("between");
    if (isRange) {
      return val;
    }
    const num = Number(val);
    if (Number.isFinite(num) && !isNaN(num)) {
      return Math.round(num);
    }
    return val;
  };

  const handleImageClick = (e, imageUrl, property) => {
    e.stopPropagation();
    setSelectedImage(appendCacheBuster(resolveImageUrl(imageUrl), property?._imgBust));
    setShowImageModal(true);
  };

  const getMLDisplayValue = (property, field) => {
    const pick = (keys) => {
      for (const k of keys) {
        const v = property?.[k];
        if (hasUsableValue(v)) return v;
      }
      return null;
    };

    switch (field) {
      case 'floor':
        return pick(["floors_ml", "floor_ml", "floors_pred", "floor_pred"]) ?? 'N/A';
      case 'landuse':
        return pick(["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]) ?? 'N/A';
      case 'landarea': {
        const ml = pick(["landarea_ml", "land_area_ml"]);
        const bucket = pick(["landarea_combined_pred"]);
        const mlNum = toFiniteNumber(ml);
        const mlIsZeroish = mlNum !== null && mlNum <= 0;
        return (hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish))
          ? bucket
          : (hasUsableValue(ml) ? ml : (bucket ?? 'N/A'));
      }
      case 'coveredarea': {
        const ml = pick(["coveredarea_ml", "covered_area_ml"]);
        const bucket = pick(["coveredarea_combined_pred"]);
        const mlNum = toFiniteNumber(ml);
        const mlIsZeroish = mlNum !== null && mlNum <= 0;
        return (hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish))
          ? bucket
          : (hasUsableValue(ml) ? ml : (bucket ?? 'N/A'));
      }
      default:
        return 'N/A';
    }
  };

  const formatAreaValue = (val) => {
    if (!hasUsableValue(val)) return "N/A";
    const n = toFiniteNumber(val);
    if (n === null) return String(val);
    return String(Math.round(n));
  };





  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-4 md:p-6 overflow-y-auto">
        <Toaster position="top-right" />

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Flagged by Computer Vision
              </h1>
              <p className="text-gray-600 mt-3 text-base italic border-l-4 border-blue-400 pl-4 bg-blue-50 py-2 rounded-r">
                Hello!
                These properties were flagged by the Computer Vision algorithm and have been reviewed by the inspector. Please go through each property and decide whether you wish to accept or reject their assessment.</p>
            </div>
            {!loading && (
              <div className="text-xs sm:text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full border border-gray-200">
                Showing <span className="font-semibold">{filteredProperties.length}</span> of{" "}
                <span className="font-semibold">{properties.length}</span> properties
              </div>
            )}
          </div>
        </div>

        {!loading && (
          <div className="mb-6 flex flex-wrap items-center gap-4">
            {/* New tabs for flagged reassessed and not reassessed */}
            <div className="flex border-b border-gray-300 mb-4 bg-white rounded-t-xl">
              <button
                onClick={() => {
                  setActiveTab("all");
                  setFilterStatus("all");
                }}
                className={`px-6 py-3 text-sm font-semibold transition-colors ${activeTab === "all"
                    ? "text-teal-600 border-b-2 border-teal-600"
                    : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                All ({(properties || []).length || 0})
              </button>
              <button
                onClick={() => setActiveTab("flagged-not-reassessed")}
                className={`px-6 py-3 text-sm font-semibold transition-colors ${activeTab === "flagged-not-reassessed"
                    ? "text-red-600 border-b-2 border-red-600"
                    : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                Not Reassessed by Inspector ({flaggedNotReassessedProperties.length})
              </button>
              <button
                onClick={() => setActiveTab("flagged-reassessed")}
                className={`px-6 py-3 text-sm font-semibold transition-colors ${activeTab === "flagged-reassessed"
                    ? "text-orange-600 border-b-2 border-orange-600"
                    : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                Reassessed by Inspector ({flaggedReassessedProperties.length})
              </button>
            </div>

            {/* Existing filter buttons only shown when activeTab is 'all' */}
            {activeTab === "all" && properties.length > 0 && (
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'all'
                      ? 'bg-white text-purple-600 shadow'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                  data-click-id="flagged-by-ai-all"
                >
                  All ({(properties || []).length || 0})
                </button>
                <button
                  onClick={() => setFilterStatus('pending')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'pending'
                      ? 'bg-white text-purple-600 shadow'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                  data-click-id="flagged-by-ai-pending"
                >
                  Pending ({pendingCount})
                </button>
                <button
                  onClick={() => setFilterStatus('responded')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === 'responded'
                      ? 'bg-white text-purple-600 shadow'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                  data-click-id="flagged-by-ai-responded"
                >
                  Responded ({respondedCount})
                </button>
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading flagged properties...</p>
            </div>
          </div>
        )}

        {!loading && filteredProperties.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow">
            <div className="text-6xl mb-6">🤖</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {activeTab === "flagged-reassessed"
                ? "No Flagged Reassessed Properties"
                : activeTab === "flagged-not-reassessed"
                  ? "No Flagged Not Reassessed Properties"
                  : filterStatus === 'responded'
                    ? 'No Responded Properties'
                    : filterStatus === 'pending'
                      ? 'No Pending Properties'
                      : 'No Algorithm-Flagged Properties'}
            </h3>
            <p className="text-gray-600">
              {activeTab === "flagged-reassessed"
                ? "There are no flagged properties that have been reassessed."
                : activeTab === "flagged-not-reassessed"
                  ? "There are no flagged properties that have not been reassessed."
                  : filterStatus === 'responded'
                    ? 'No inspectors have responded to any flagged properties yet.'
                    : filterStatus === 'pending'
                      ? 'All flagged properties have been responded to.'
                      : 'There are no properties flagged by the algorithm at this time.'}
            </p>
          </div>
        )}

        {!loading && filteredProperties.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProperties.map((property) => {
              const isSelected = normalizePin(property.pin) && normalizePin(property.pin) === highlightPin;

              return (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-pin={normalizePin(property.pin)}
                  className={`bg-white shadow hover:shadow-lg cursor-pointer transition-all relative rounded-xl ${isSelected ? "border-2 border-sky-500" : "border border-gray-200"
                    }`}
                  onClick={() => {
                    setSelectedProperty(property);
                    setSelectedPropertyOrigin(
                      activeTab === "flagged-reassessed" || activeTab === "flagged-not-reassessed"
                        ? "algorithm"
                        : "algorithm"
                    );
                    setShowDetailModal(true);
                    setRotation(0);
                    fetchPropertyTimeline(property.pin);
                  }}
                >
                  {isSelected && (
                    <div className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold shadow">
                      ✓
                    </div>
                  )}
                  <div className="h-48 bg-gray-100 overflow-hidden relative group rounded-t-xl">
                    {property.image ? (
                      <>
                        <img
                          src={appendCacheBuster(resolveImageUrl(property.image), property._imgBust)}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                          alt="Property"
                        />
                        <div
                          className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          onClick={(e) => handleImageClick(e, property.image, property)}
                        >
                          <div className="bg-white/90 p-3 rounded-full">
                            <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
                        <span className="text-4xl">🏢</span>
                      </div>
                    )}

                    {property.confidence_score && Math.round(property.confidence_score * 100) < 100 && (
                      <div className="absolute top-3 right-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getConfidenceColor(property.confidence_score)}`}>
                          {Math.round(property.confidence_score * 100)}% Match
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <h3 className="font-semibold text-lg text-gray-900 mb-2">
                      PIN: {property.pin}
                    </h3>

                    <div className="space-y-2 text-sm text-gray-600">
                      <div>Circle: {property.circle || 'N/A'}</div>
                      {/*  <div>Inspector: {property.inspector_name || property.inspector_username || 'N/A'}</div> */}

                      {/*  <div className="grid grid-cols-1 gap-1 mt-3 py-2 border-y border-gray-100 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Floor (Admin/Computer Vision):</span>
                          <span className="font-medium">
                            {hasUsableValue(property.floor_admin) ? property.floor_admin : "N/A"} /{" "}
                            {formatMLValue(getMLDisplayValue(property, "floor"))}                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Land Use (Admin/Computer Vision):</span>
                          <span className="font-medium truncate ml-2 text-right max-w-[120px]">
                            {hasUsableValue(property.landuse_admin) ? property.landuse_admin : "N/A"} /{" "}
                            {formatMLValue(getMLDisplayValue(property, "landuse"))}                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Land Area (Admin/Computer Vision):</span>
                          <span className="font-medium">{formatAreaValue(property.landarea_admin)} / {formatAreaValue(getMLDisplayValue(property, 'landarea'))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Covered Area (Admin/Computer Vision):</span>
                          <span className="font-medium">{formatAreaValue(property.coveredarea_admin)} / {formatAreaValue(getMLDisplayValue(property, 'coveredarea'))}</span>
                        </div>
                      </div> */}

                      {property.inspector_response && (
                        <div className="mt-2 p-2 bg-green-50 rounded-lg">
                          <span className="text-green-700 text-xs font-semibold">
                            ✓ Responded on {new Date(property.response_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2">
                      <button
                        className="flex-1 py-2 text-sm font-medium text-purple-600 hover:text-purple-800 transition-colors"
                        data-click-id="flagged-by-ai-view-details"
                        data-property-id={property.pin}
                      >
                        View Details →
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <AnimatePresence>
          {showDetailModal && selectedProperty && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white w-full h-full shadow-2xl flex flex-col relative"
              >
                <div className="flex-shrink-0 p-6 border-b border-gray-200 pr-14">
                  <button
                    onClick={closeDetailModal}
                    className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-10 flex items-center justify-center w-10 h-10"
                    data-click-id="flagged-by-ai-close"
                    data-property-id={selectedProperty.pin}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-gray-500 text-sm">Circle</p>
                      <p className="text-lg font-bold text-gray-800 uppercase">{selectedProperty.circle || "N/A"}</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-500">
                    <p>
                      PIN: <span className="font-semibold text-gray-700">{selectedProperty.pin}</span>
                    </p>
                    {/*  <p>
                      PT-5 Reference:{" "}
                      <span className="font-semibold text-gray-700">
                        {getPt5Reference(selectedProperty) || "N/A"}
                      </span>
                    </p> */}
                    <p>
                      Inspector:{" "}
                      <span className="font-semibold text-gray-700">
                        {selectedProperty.inspector_name || selectedProperty.inspector_username || "N/A"}
                      </span>
                    </p>
                    {selectedProperty.flagged_date && (
                      <p>
                        Flagged:{" "}
                        <span className="font-semibold text-gray-700">
                          {new Date(selectedProperty.flagged_date).toLocaleDateString()}
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-6 mb-6">
                    <div className="relative group bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer h-[350px]">
                      {selectedProperty.image ? (
                        <>
                          <div
                            className="w-full h-full transition-transform duration-300 ease-in-out flex items-center justify-center"
                            style={{ transform: `rotate(${rotation}deg)` }}
                            onClick={() => {
                              setSelectedImage(appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust));
                              setShowImageModal(true);
                            }}
                          >
                            <img
                              src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                              className="max-w-full max-h-full object-contain hover:opacity-90 transition-opacity"
                              alt="Property"
                            />
                          </div>
                          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                            <span className="text-white bg-black/50 px-3 py-1 rounded-full text-sm font-medium">Click to Enlarge</span>
                          </div>
                          <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                            <button
                              onClick={handleRotate}
                              className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                              title="Rotate Image"
                            >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                            {rotation !== 0 && (
                              <button
                                onClick={handleSaveRotation}
                                disabled={savingImageRotation}
                                className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                                title="Save Rotation"
                              >
                                {savingImageRotation ? (
                                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-lg text-gray-500">
                          No Image
                        </div>
                      )}
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-gray-800">Property Information</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border rounded-lg overflow-hidden text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="p-3 text-left font-medium">Feature</th>
                              <th className="p-3 text-left font-medium">Admin</th>
                              <th className="p-3 text-left font-medium">Computer Vision</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              { label: 'Floor', admin: selectedProperty.floor_admin, ml: getMLDisplayValue(selectedProperty, 'floor'), field: 'floor' },
                              { label: 'Land Use', admin: selectedProperty.landuse_admin, ml: getMLDisplayValue(selectedProperty, 'landuse'), field: 'landuse' },
                              { label: 'Land Area', admin: selectedProperty.landarea_admin, ml: getMLDisplayValue(selectedProperty, 'landarea'), field: 'landarea' },
                              { label: 'Covered Area', admin: selectedProperty.coveredarea_admin, ml: getMLDisplayValue(selectedProperty, 'coveredarea'), field: 'coveredarea' },
                            ].map((row) => {
                              const isArea = row.label === 'Land Area' || row.label === 'Covered Area';
                              const isDifferent = areComparableValuesDifferent(row.admin, row.ml, row.field);
                              return (
                                <tr key={row.label} className={`border-b ${isDifferent ? 'bg-red-100' : ''}`}>
                                  <td className="p-3 font-medium">{row.label}</td>
                                  <td className="p-3">
                                    {isArea
                                      ? (() => {
                                        const v = formatAreaValue(row.admin);
                                        if (v === "N/A") return "N/A";
                                        return `${v} ${row.label === "Land Area" ? "sq.yards" : "sq.ft"}`;
                                      })()
                                      : (row.admin === null || row.admin === undefined || String(row.admin).trim() === '' ? 'N/A' : row.admin)}
                                  </td>
                                  <td className="p-3">
                                    {isArea ? formatAreaValue(row.ml) : formatMLValue(row.ml)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {selectedProperty.inspector_response && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="font-semibold text-green-800">
                          ✓ Inspector Response ({new Date(selectedProperty.response_date).toLocaleDateString()})
                        </h3>
                      </div>
                      {(selectedProperty.agree_with_ai === 0 || selectedProperty.agree_with_ai === 1) && (
                        <p className="text-green-800 text-sm mb-2">
                          Agrees with AI: <strong>{selectedProperty.agree_with_ai ? 'Yes' : 'No'}</strong>
                        </p>
                      )}
                      <p className="text-green-700">{selectedProperty.inspector_response}</p>
                    </div>
                  )}

                  {/* Timeline / Chat History */}
                  {SHOW_ACTIVITY_HISTORY && propertyTimeline.timeline && propertyTimeline.timeline.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-3 text-gray-800">Activity History & Chat</h3>
                      <div className="space-y-3">
                        {propertyTimeline.timeline.map((item) => {
                          const isInspector = item.user_role === "inspector";
                          const isSupervisor = item.user_role === "supervisor";

                          return (
                            <div key={item.id} className="flex gap-3">
                              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${isInspector ? "bg-blue-500" : isSupervisor ? "bg-green-500" : "bg-gray-500"
                                }`}>
                                {item.user_name ? item.user_name.charAt(0).toUpperCase() : "?"}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-semibold text-gray-800">
                                    {item.user_name || "Unknown User"}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(item.created_at).toLocaleString()}
                                  </span>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                    {item.stage}
                                  </span>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3">
                                  {item.type === "comment" && (
                                    <p className="text-sm text-gray-700">{item.text}</p>
                                  )}
                                  {item.type === "supervisor" && (
                                    <p className="text-sm text-gray-700">{item.text}</p>
                                  )}
                                  {item.type === "inspector_explanation" && (
                                    <div className="text-sm text-gray-700">
                                      <p className="font-medium mb-2">Overall: {item.overall}</p>
                                      {item.rows.floor && <p>Floor: {item.rows.floor}</p>}
                                      {item.rows.landuse && <p>Land Use: {item.rows.landuse}</p>}
                                      {item.rows.landarea && <p>Land Area: {item.rows.landarea}</p>}
                                      {item.rows.coveredarea && <p>Covered Area: {item.rows.coveredarea}</p>}
                                    </div>
                                  )}
                                  {item.type === "action" && (
                                    <p className="text-sm text-gray-700">
                                      {item.action.replace(/_/g, " ")}
                                      {item.details && (
                                        <span className="ml-2 text-gray-500">
                                          - {JSON.stringify(item.details)}
                                        </span>
                                      )}
                                    </p>
                                  )}
                                  {item.type === "algorithm_flag" && (
                                    <p className="text-sm text-gray-700">{item.text}</p>
                                  )}
                                  {item.type === "algorithm_response" && (
                                    <p className="text-sm text-gray-700">{item.text}</p>
                                  )}
                                  {item.type === "algorithm_rejected" && (
                                    <p className="text-sm text-gray-700">{item.text}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-shrink-0 flex justify-end gap-4 p-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleRejectClick}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg font-medium shadow hover:bg-red-600 transition"
                  >
                    Reject
                  </button>
                  <button
                    onClick={handleAccept}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg font-medium shadow hover:bg-gray-600 transition"
                  >
                    Accept
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showImageModal && selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
              onClick={() => setShowImageModal(false)}
            >
              <button
                onClick={() => setShowImageModal(false)}
                className="absolute top-4 right-4 p-2 text-white hover:text-gray-300 transition-colors z-10"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <motion.img
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                src={selectedImage}
                alt="Full size property"
                className="max-w-full max-h-full object-contain"
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showRejectModal && selectedProperty && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
              onClick={handleRejectCancel}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4"
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Reject Property</h2>
                <p className="text-gray-600 mb-6">
                  Please provide a reason for rejection. This will be shared with the inspector.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  className="w-full p-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent mb-6 min-h-[120px]"
                  autoFocus
                />
                <div className="flex justify-end gap-4">
                  <button
                    onClick={handleRejectCancel}
                    className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium rounded-lg transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRejectSubmit}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg font-medium shadow hover:bg-red-600 transition"
                  >
                    Submit Rejection
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default FlaggedByAI;